import type { CmChecklist } from '@luxuryescapes/contract-svc-offer';

import { isFiniteNumber } from '../../shared/checklistUtils';
import { formatGuestTypeCounts, formatRoomSize } from '../../shared/format';
import type { RoomDetailView, RoomRowView, UnlinkedDealOptionView } from '../../tabs/rooms/types';
import type { WithChecklistItemMetadata } from '../../types';
import { presentRowBase } from '../rowBase';

export interface PresentedRooms {
  rows: Array<WithChecklistItemMetadata<RoomRowView>>;
  unlinkedDealOptions: Array<UnlinkedDealOptionView>;
}

// Unlinked deal options have no room row, so they are derived from the deal option approvals.
export function presentRoomRows({
  approvalRows,
  roomRows,
}: {
  approvalRows: Array<CmChecklist.DealOptionApprovalRow>;
  roomRows: Array<CmChecklist.RoomRow>;
}): PresentedRooms {
  return {
    rows: roomRows.map(presentRoomRow),
    unlinkedDealOptions: approvalRows
      .filter((row) => !row.subject.room_asset_linked)
      .map((row) => ({
        dealId: row.subject.deal_id,
        dealOptionName: row.subject.deal_option_name ?? row.subject.deal_id,
      })),
  };
}

function presentRoomRow(row: CmChecklist.RoomRow): WithChecklistItemMetadata<RoomRowView> {
  const name = row.subject.room_name ?? `Unnamed room (${row.subject.room_asset_id})`;
  const differingDealIds = getDifferingDealIds(row.evidence.allocation_groups);
  const mismatchedOccupancies = row.evidence.allocation_groups
    .filter((group) => hasAllocationMismatch(group))
    .map((group) => group.occupancy);

  return {
    ...presentRowBase(row),
    capacity: [
      // Included guests are per deal option (its occupancy column), so the room shows only its maximum.
      {
        label: 'Max',
        value: formatGuestTypeCounts(row.subject.capacity_breakdown.max) ?? row.subject.max_occupancy_text ?? '—',
      },
    ],
    dealOptions: row.subject.deal_options.map((dealOption) => ({
      allocation: dealOption.allocation,
      allocationLabel: dealOption.allocation_text ?? 'Not set',
      dealId: dealOption.deal_id,
      dealOptionName: dealOption.deal_option_name ?? dealOption.deal_id,
      differs: differingDealIds.has(dealOption.deal_id),
      occupancy: dealOption.occupancy,
    })),
    expandedDetails: presentExpandedRoomDetails(row.subject.room_details),
    details: presentRoomDetails(row.subject.room_details),
    field: 'Room',
    name,
    roomAssetId: row.subject.room_asset_id,
    summary:
      row.reason_codes.includes('allocation_mismatch') && mismatchedOccupancies.length > 0
        ? `Allocation differs across deal options for ${name} · ${mismatchedOccupancies.join(', ')}.`
        : 'Allocations match across deal options at the same occupancy.',
  };
}

// CMs often type a placeholder instead of leaving the field blank.
const BLANK_PLACEHOLDERS = new Set(['n/a', 'na', 'none', '-']);

function withoutPlaceholder(value: string | null) {
  return value && !BLANK_PLACEHOLDERS.has(value.trim().toLowerCase()) ? value : undefined;
}

function presentRoomDetails(roomDetails: CmChecklist.RoomDetails): Array<RoomDetailView> {
  return [
    { label: 'Room size', value: formatRoomSize(roomDetails.room_size) ?? '—' },
    // Some values carry line breaks between alternatives ("1 Queen OR\r2 Queen Beds").
    { label: 'Bedding', value: roomDetails.standard_bedding?.replace(/\s+/g, ' ').trim() || '—' },
    {
      label: 'Additional bedding',
      shortLabel: 'Add. Bed.',
      value: withoutPlaceholder(roomDetails.additional_bedding_type) ?? '—',
    },
  ];
}

function presentExpandedRoomDetails(roomDetails: CmChecklist.RoomDetails): Array<RoomDetailView> {
  return [
    { label: 'View', value: withoutPlaceholder(roomDetails.room_view) },
    { label: 'Additional bedding information', value: withoutPlaceholder(roomDetails.additional_bedding_information) },
  ].flatMap(({ label, value }) => (value ? [{ label, value }] : []));
}

function hasAllocationMismatch(group: CmChecklist.RoomAllocationGroup) {
  return new Set(group.allocations.map((entry) => entry.allocation).filter(isFiniteNumber)).size > 1;
}

function getDifferingDealIds(groups: Array<CmChecklist.RoomAllocationGroup>) {
  const differingDealIds = new Set<string>();

  for (const group of groups.filter(hasAllocationMismatch)) {
    const sharedAllocation = getSharedAllocation(group.allocations.map((entry) => entry.allocation));
    for (const entry of group.allocations) {
      if (isFiniteNumber(entry.allocation) && entry.allocation !== sharedAllocation) {
        differingDealIds.add(entry.deal_id);
      }
    }
  }

  return differingDealIds;
}

// The allocation most deal options share; undefined on a tie, so every value is highlighted.
function getSharedAllocation(allocations: Array<number | null>) {
  const allocationCounts = new Map<number, number>();
  for (const allocation of allocations.filter(isFiniteNumber)) {
    allocationCounts.set(allocation, (allocationCounts.get(allocation) ?? 0) + 1);
  }

  const allocationsByFrequency = [...allocationCounts.entries()].sort((a, b) => b[1] - a[1]);
  const [mostCommon, runnerUp] = allocationsByFrequency;
  return mostCommon && mostCommon[1] !== runnerUp?.[1] ? mostCommon[0] : undefined;
}
